import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { GatedLeader } from "@/hooks/useLeaderGate";

interface UpgradeBannerProps {
  leaderCount: number;
  leaderLimit: number;
  plan: string;
  overBy: number;
  softGatedCount: number;
  hardGatedCount: number;
}

export function UpgradeBanner({
  leaderCount,
  leaderLimit,
  plan,
  overBy,
  softGatedCount,
  hardGatedCount,
}: UpgradeBannerProps) {
  const navigate = useNavigate();

  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">
          You have {leaderCount} leaders but your {plan === "free" ? "Free" : "Growth"} plan
          {plan === "free" ? " includes 1 leader" : ` supports up to ${leaderLimit}`}.
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {softGatedCount > 0 && (
            <>
              {softGatedCount} leader{softGatedCount > 1 ? "s have" : " has"} a 7-day grace period — upgrade before access is blocked.
            </>
          )}
          {softGatedCount > 0 && hardGatedCount > 0 && " "}
          {hardGatedCount > 0 && (
            <>
              {hardGatedCount} leader{hardGatedCount > 1 ? "s are" : " is"} currently blocked.
            </>
          )}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => navigate("/pricing")}
        >
          View Plans
          <ArrowRight className="ml-2 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** Inline grace period badge shown on individual leader cards */
export function GracePeriodBadge({ gatedLeader }: { gatedLeader: GatedLeader }) {
  if (gatedLeader.isHardGated) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-xs font-medium">
        <AlertTriangle className="h-3 w-3" />
        Access blocked
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-xs font-medium">
      <Clock className="h-3 w-3" />
      {gatedLeader.graceDaysLeft} day{gatedLeader.graceDaysLeft !== 1 ? "s" : ""} left
    </span>
  );
}
